import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { completePendingAction } from '../utils/pendingAction';
import GoogleLoginButton from '../components/GoogleLoginButton';
import PasswordInput from '../components/PasswordInput';
import './Auth.css';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  // If already logged in, redirect to home (and replace history so back button doesn't return to login)
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Show message when redirected here after 401 (e.g. session expired, or guest tried protected action)
  useEffect(() => {
    const message = sessionStorage.getItem('loginRedirectMessage');
    if (message) {
      sessionStorage.removeItem('loginRedirectMessage');
      toast.info(message);
    }
  }, []);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [mobileLogin, setMobileLogin] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/user/auth/login', formData);
      login(response.token, response.user);
      toast.success('Logged in successfully');
      const didComplete = await completePendingAction(navigate);
      if (!didComplete) navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMobileLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!otpSent) {
        // Send OTP
        await api.post('/user/auth/mobile-login', { phone });
        setOtpSent(true);
        toast.success('OTP sent to your phone');
      } else {
        // Verify OTP and login
        const response = await api.post('/user/auth/mobile-login', { phone, otp });
        login(response.token, response.user);
        toast.success('Logged in successfully');
        const didComplete = await completePendingAction(navigate);
        if (!didComplete) navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Mobile login failed');
    } finally {
      setLoading(false);
    }
  };


  if (!authLoading && isAuthenticated) {
    return null; // redirecting in useEffect
  }

  return (
    <div className="auth-page">
      <Container>
        <div className="auth-card">
          <h1 className="auth-title">Login To Your Account</h1>
          <p className="auth-subtitle">Welcome Back! Please Enter Your Details.</p>

          {!mobileLogin ? (
            <>
              <GoogleLoginButton isSignup={false} />

              <div className="divider">
                <span>OR</span>
              </div>

              <Form onSubmit={handleEmailLogin}>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email"
                    className="auth-input"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <PasswordInput
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Password"
                  />
                </Form.Group>

                <div className="forgot-password">
                  <Link to="/forgot-password">Forgot Password?</Link>
                </div>

                <Button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? 'Logging in...' : 'LOGIN'}
                </Button>
              </Form>

              <div className="auth-switch">
                <p>
                  Don't Have An Account? <Link to="/signup">Register</Link>
                </p>
              </div>

              {/* <div className="text-center mt-3">
                <Button variant="link" onClick={() => setMobileLogin(true)} className="mobile-login-link">
                  Login with Mobile
                </Button>
              </div> */}
            </>
          ) : (
            <Form onSubmit={handleMobileLogin}>
              <Form.Group className="mb-3">
                <Form.Control
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder="Phone Number"
                  disabled={otpSent}
                  className="auth-input"
                />
              </Form.Group>

              {otpSent && (
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="auth-input"
                  />
                </Form.Group>
              )}

              <Button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading
                  ? 'Processing...'
                  : otpSent
                  ? 'Verify & Login'
                  : 'Send OTP'}
              </Button>

              <div className="text-center mt-3">
                <Button variant="link" onClick={() => {
                  setMobileLogin(false);
                  setOtpSent(false);
                  setPhone('');
                  setOtp('');
                }} className="mobile-login-link">
                  Back to Email Login
                </Button>
              </div>
            </Form>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Login;

